import React, { useMemo } from 'react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';

interface SearchContactConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
  onBatchChange?: (updates: Record<string, any>) => void;
}

type SearchField = 'phone' | 'email' | 'custom';

function normalizeEntityFields(rawFields: any): Array<{ id: string; label: string }> {
  if (!rawFields || typeof rawFields !== 'object') return [];
  return Object.entries(rawFields)
    .map(([id, field]: [string, any]) => ({
      id,
      label: String(field?.formLabel || field?.title || id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const SearchContactConfig: React.FC<SearchContactConfigProps> = ({
  config,
  onChange,
  onBatchChange,
}) => {
  const { bitrixFields } = useFlowBuilderStore();

  const searchIn = config.searchIn === 'deal' ? 'deal' : config.searchIn === 'lead' ? 'lead' : 'contact_company';
  const searchField: SearchField =
    config.searchField === 'custom' ? 'custom' : config.searchField === 'email' ? 'email' : 'phone';
  const searchValue = config.searchValue ?? config.phoneNumber ?? (searchField === 'email' ? '{{sender_email}}' : '{{sender_phone}}');
  const dealCategories = Array.isArray(bitrixFields?.dealCategories) ? bitrixFields.dealCategories : [];

  // Fields available for custom search depending on the entity
  const customFields = useMemo(() => {
    if (searchIn === 'lead') return normalizeEntityFields(bitrixFields?.lead);
    if (searchIn === 'deal') return normalizeEntityFields(bitrixFields?.deal);
    // contact_company: contact custom fields (the "Contato" picker)
    return normalizeEntityFields(bitrixFields?.contact);
  }, [bitrixFields, searchIn]);

  // Company custom fields — used only for the "Empresa" picker in contact_company mode
  // (contact and company custom fields are distinct entities in Bitrix).
  const companyCustomFields = useMemo(
    () => normalizeEntityFields(bitrixFields?.company),
    [bitrixFields],
  );

  const setField = (nextField: SearchField) => {
    const updates: Record<string, any> = { searchField: nextField };
    if (nextField !== 'custom') {
      const nextDefault = nextField === 'email' ? '{{sender_email}}' : '{{sender_phone}}';
      if (!String(config.searchValue || config.phoneNumber || '').trim()) {
        updates.searchValue = nextDefault;
        updates.phoneNumber = nextDefault;
      }
    }
    if (onBatchChange) {
      onBatchChange(updates);
    } else {
      Object.entries(updates).forEach(([k, v]) => onChange(k, v));
    }
  };

  const setSearchValue = (val: string) => {
    if (onBatchChange) {
      onBatchChange({ searchValue: val, phoneNumber: val });
    } else {
      onChange('searchValue', val);
      onChange('phoneNumber', val);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Como funciona?</h4>
        {searchIn === 'deal' ? (
          <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc list-inside space-y-1">
            <li>Se encontrar <strong>Negócio</strong>, segue pela saída <span className="font-medium text-purple-600 dark:text-purple-400">Negócio</span>.</li>
            <li>Se não encontrar, segue por <span className="font-medium text-red-600 dark:text-red-400">Nada</span>.</li>
            <li>Preenche: <code>{'{{found_entity_id}}'}</code> e <code>{'{{deal_id}}'}</code>.</li>
          </ul>
        ) : searchIn === 'lead' ? (
          <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc list-inside space-y-1">
            <li>Se encontrar <strong>Lead</strong>, segue pela saída <span className="font-medium text-orange-600 dark:text-orange-400">Lead</span>.</li>
            <li>Se não encontrar, segue por <span className="font-medium text-red-600 dark:text-red-400">Nada</span>.</li>
            <li>Preenche: <code>{'{{found_entity_id}}'}</code>, <code>{'{{lead_id}}'}</code>, <code>{'{{lead.NAME}}'}</code>.</li>
          </ul>
        ) : (
          <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc list-inside space-y-1">
            <li>Se encontrar <strong>Contato</strong>, segue por <span className="font-medium text-green-600 dark:text-green-400">Contato</span>.</li>
            <li>Se encontrar <strong>Empresa</strong>, segue por <span className="font-medium text-blue-600 dark:text-blue-400">Empresa</span>.</li>
            <li>Se não encontrar, segue por <span className="font-medium text-red-600 dark:text-red-400">Nada</span>.</li>
            <li>Preenche: <code>{'{{found_entity_id}}'}</code>, <code>{'{{contact_id}}'}</code>, <code>{'{{company_id}}'}</code>.</li>
          </ul>
        )}
      </div>

      {/* Buscar em */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Buscar em</label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={searchIn}
          onChange={(e) => {
            const v = e.target.value;
            onChange('searchIn', v === 'deal' ? 'deal' : v === 'lead' ? 'lead' : 'contact_company');
            // Reset custom field when entity changes
            if (searchField === 'custom') onChange('customFieldId', '');
          }}
        >
          <option value="contact_company">Contato / Empresa</option>
          <option value="deal">Negócio</option>
          <option value="lead">Lead</option>
        </select>
      </div>

      {/* Buscar por */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Buscar por</label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={searchField}
          onChange={(e) => setField(e.target.value as SearchField)}
        >
          <option value="phone">Telefone</option>
          <option value="email">E-mail</option>
          <option value="custom">Campo personalizado</option>
        </select>
      </div>

      {/* Campo personalizado picker */}
      {searchField === 'custom' && searchIn === 'contact_company' && (
        <div className="space-y-3">
          {/* Contato */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
              Campo do CRM
              <span className="ml-1 text-slate-400 dark:text-slate-500 font-normal">(Contato)</span>
            </label>
            {customFields.length > 0 ? (
              <select
                className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                value={config.customFieldId || ''}
                onChange={(e) => onChange('customFieldId', e.target.value)}
              >
                <option value="">Não buscar em Contato</option>
                {customFields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label} ({f.id})
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Nenhum campo de Contato disponível. Verifique a conexão com o Bitrix24.
              </p>
            )}
          </div>
          {/* Empresa */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
              Campo do CRM
              <span className="ml-1 text-slate-400 dark:text-slate-500 font-normal">(Empresa)</span>
            </label>
            {companyCustomFields.length > 0 ? (
              <select
                className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                value={config.companyCustomFieldId || ''}
                onChange={(e) => onChange('companyCustomFieldId', e.target.value)}
              >
                <option value="">Não buscar em Empresa</option>
                {companyCustomFields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label} ({f.id})
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Nenhum campo de Empresa disponível. Verifique a conexão com o Bitrix24.
              </p>
            )}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            Escolha o campo de <strong>Contato</strong> e/ou de <strong>Empresa</strong>. A busca tenta o Contato primeiro e depois a Empresa. Deixe "Não buscar" para ignorar aquela entidade.
          </p>
        </div>
      )}

      {searchField === 'custom' && searchIn !== 'contact_company' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
            Campo do CRM
            <span className="ml-1 text-slate-400 dark:text-slate-500 font-normal">
              ({searchIn === 'lead' ? 'Lead' : 'Negócio'})
            </span>
          </label>
          {customFields.length > 0 ? (
            <select
              className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
              value={config.customFieldId || ''}
              onChange={(e) => onChange('customFieldId', e.target.value)}
            >
              <option value="">Selecione um campo</option>
              {customFields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label} ({f.id})
                </option>
              ))}
            </select>
          ) : (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Nenhum campo disponível. Verifique a conexão com o Bitrix24.
            </p>
          )}
        </div>
      )}

      {/* Modo de comparação */}
      {searchField === 'custom' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Modo de busca</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
            value={config.customFieldMatchMode || 'exact'}
            onChange={(e) => onChange('customFieldMatchMode', e.target.value)}
          >
            <option value="exact">Igual (exato)</option>
            <option value="contains">Contém (parcial)</option>
          </select>
        </div>
      )}

      {/* Normalização de telefone BR */}
      {searchField === 'custom' && (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 accent-indigo-600"
            checked={!!config.normalizeBrPhone}
            onChange={(e) => onChange('normalizeBrPhone', e.target.checked)}
          />
          <span className="text-xs text-slate-700 dark:text-slate-200">
            Normalizar telefone BR
            <span className="block text-slate-400 dark:text-slate-500 font-normal">
              Adiciona o nono dígito se ausente — resolve diferença de formato entre WhatsApp e CRM
            </span>
          </span>
        </label>
      )}

      {/* Pipeline do Negócio */}
      {searchIn === 'deal' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Pipeline do Negócio</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
            value={config.dealPipelineId ?? 'all'}
            onChange={(e) => onChange('dealPipelineId', e.target.value)}
          >
            <option value="all">Todos os pipelines</option>
            {dealCategories.map((category: any) => (
              <option key={String(category.ID)} value={String(category.ID)}>
                {category.NAME || `Pipeline ${category.ID}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Status do Negócio */}
      {searchIn === 'deal' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">Status do Negócio</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
            value={config.dealStatusFilter ?? 'any'}
            onChange={(e) => onChange('dealStatusFilter', e.target.value)}
          >
            <option value="any">Misto (abertos e fechados)</option>
            <option value="open">Somente abertos</option>
            <option value="closed">Somente fechados</option>
          </select>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            Filtra por situação do negócio: <strong>abertos</strong> (em andamento), <strong>fechados</strong> (ganhos ou perdidos) ou <strong>misto</strong> (ignora o status).
          </p>
        </div>
      )}

      {/* Valor para busca */}
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          {searchField === 'email'
            ? 'E-mail para busca'
            : searchField === 'custom'
              ? 'Valor para busca'
              : 'Telefone para busca'}
        </label>
        <input
          type="text"
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={searchField === 'custom' ? (config.searchValue || '') : searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={
            searchField === 'email'
              ? '{{sender_email}}'
              : searchField === 'custom'
                ? 'Ex: {{cpf_cliente}}'
                : '{{sender_phone}}'
          }
        />
      </div>

      <p className="text-[11px] text-slate-500 dark:text-slate-300">
        O ID principal encontrado fica disponível em <code>{'{{found_entity_id}}'}</code>.
      </p>

      {searchField === 'custom' && (
        <p className="text-[11px] text-slate-500 dark:text-slate-300">
          A busca usa <code>crm.*.list</code> com filtro exato ou "contém" no campo selecionado, conforme o <strong>Modo de busca</strong>.
          Use variáveis como <code>{'{{cpf_cliente}}'}</code> no valor.
        </p>
      )}
      {searchIn === 'deal' && searchField !== 'custom' && (
        <p className="text-[11px] text-slate-500 dark:text-slate-300">
          A busca em Negócio usa Contato/Empresa relacionados ao {searchField === 'email' ? 'e-mail' : 'telefone'} para encontrar o negócio no pipeline escolhido.
        </p>
      )}
      {searchIn === 'lead' && searchField !== 'custom' && (
        <p className="text-[11px] text-slate-500 dark:text-slate-300">
          Use <code>{'{{lead.NAME}}'}</code>, <code>{'{{lead.LAST_NAME}}'}</code>, <code>{'{{lead_id}}'}</code>.
        </p>
      )}
    </div>
  );
};
